import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as OptimizeIcon,
  PostAdd as PostIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  LocationOn as RankingIcon,
  Business as BusinessIcon,
  Insights as InsightsIcon,
  LightbulbOutlined as SuggestionsIcon,
  RateReview as ReviewsIcon,
  Analytics as PerformanceIcon,
  TrendingUp,
  BuildOutlined as ServiceIcon,
  PhotoLibrary as MediaIcon,
  People as UsersIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useBusinessSearch } from '../../contexts/BusinessSearchContext';
import BusinessDropdown from '../common/BusinessDropdown';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, logout, getRemainingTrialDays, isTrialExpired, isAdmin, isReadOnly, getRoleLabel } = useAuth();
  const { selectedBusiness } = useBusiness();
  const { minimalBusinesses, loading: businessLoading, selectBusiness, refreshBusinesses } = useBusinessSearch();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Get the locationId from selectedBusiness - prioritize locationId, then googleLocationId, then id
  const getSelectedBusinessLocationId = () => {
    if (!selectedBusiness) return null;
    return selectedBusiness.locationId || selectedBusiness.googleLocationId || selectedBusiness.id;
  };

  const businessLocationId = getSelectedBusinessLocationId();

  const allNavigationItems = [
    { text: 'Agency-Dashboard', icon: <DashboardIcon />, path: '/agency-dashboard', dividerAfter: true },
    { text: 'Profil', icon: <BusinessIcon />, path: businessLocationId ? `/profile/${businessLocationId}` : '/profile/11262821191320538409' },
    { text: 'Posting-Manager', icon: <PostIcon />, path: businessLocationId ? `/posts/${businessLocationId}` : '/posts/11262821191320538409' },
    { text: 'Ranking-Tracker', icon: <RankingIcon />, path: businessLocationId ? `/ranking/${businessLocationId}` : '/ranking/11262821191320538409' },
    { text: 'Business Insights', icon: <InsightsIcon />, path: businessLocationId ? `/insights/${businessLocationId}` : '/insights/11262821191320538409' },
    { text: 'Ranking Vorschläge', icon: <SuggestionsIcon />, path: businessLocationId ? `/suggestions/${businessLocationId}` : '/suggestions/11262821191320538409' },
    { text: 'Bewertungen', icon: <ReviewsIcon />, path: businessLocationId ? `/reviews/${businessLocationId}` : '/reviews/11262821191320538409' },
    { text: 'Medien', icon: <MediaIcon />, path: businessLocationId ? `/medias/${businessLocationId}` : '/medias/11262821191320538409' },
    { text: 'Media Approval', icon: <MediaIcon />, path: businessLocationId ? `/media-approval/${businessLocationId}` : '/media-approval/11262821191320538409' },
    { text: 'Services', icon: <ServiceIcon />, path: businessLocationId ? `/services/${businessLocationId}` : '/services/11262821191320538409' },
    { text: 'Library', icon: <MediaIcon />, path: businessLocationId ? `/library/${businessLocationId}` : '/library/11262821191320538409' },
    { text: 'Top Performer', icon: <PerformanceIcon />, path: '/top-performers' },
    { text: 'Keyword Performance', icon: <TrendingUp />, path: '/keyword-performance' },
    { text: 'Alle Unternehmen', icon: <BusinessIcon />, path: '/all-businesses' },
    { text: 'Aktivitäten', icon: <TrendingUp />, path: businessLocationId ? `/activity/${businessLocationId}` : '/activity/11262821191320538409' },
    { text: 'Bericht', icon: <TrendingUp />, path: businessLocationId ? `/report/${businessLocationId}` : '/report/11262821191320538409' },
    { text: 'Action Queue', icon: <RefreshIcon />, path: '/action-queue' },
    { text: 'Crons', icon: <ScheduleIcon />, path: '/crons', dividerAfter: true },
    { text: 'Benutzer verwalten', icon: <UsersIcon />, path: '/admin/users', adminOnly: true },
    { text: 'Einstellungen', icon: <SettingsIcon />, path: '/settings', adminOnly: true },
  ];

  // Filter navigation items based on user role
  let navigationItems = allNavigationItems.filter(item => {
    // Hide admin-only items from non-admins
    if (item.adminOnly && !isAdmin()) {
      return false;
    }
    return true;
  });


  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" color="primary" fontWeight="bold">
          A&M LokalMeister
        </Typography>
      </Toolbar>
      <Divider />
      
      {/* Trial status */}
      {user?.subscription?.status === 'trial' && (
        <Box sx={{ p: 2 }}>
          <Chip
            label={`${getRemainingTrialDays()} Tage kostenlos`}
            color={getRemainingTrialDays() <= 7 ? 'warning' : 'success'}
            size="small"
            sx={{ width: '100%' }}
          />
        </Box>
      )}
      
      <List>
        {navigationItems.map((item, index) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                disabled={false}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
            {item.dividerAfter && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </List>
      
      <Divider />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          borderRadius: 0,
          overflow: 'visible',
          '& .MuiAppBar-root': {
            borderRadius: 0
          }
        }}
      >
        <Toolbar sx={{ overflow: 'visible', position: 'relative' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* 🚀 Facebook Ads Manager Style Business Dropdown */}
          <Box sx={{ ml: 3, display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
            <Box sx={{ position: 'relative', zIndex: 100 }}>
              {businessLoading ? (
                <Skeleton
                  variant="rounded"
                  width={280}
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              ) : minimalBusinesses.length > 0 ? (
                <BusinessDropdown
                  businesses={minimalBusinesses}
                  selectedBusiness={selectedBusiness}
                  onBusinessSelect={selectBusiness}
                  loading={businessLoading}
                  placeholder="Konto auswählen..."
                  width={280}
                />
              ) : null}
            </Box>

            {minimalBusinesses.length > 0 && (
              <IconButton
                size="small"
                onClick={() => refreshBusinesses()}
                disabled={businessLoading}
                title="Geschäftsprofile aktualisieren"
                sx={{
                  position: 'relative',
                  zIndex: 101,
                  color: 'text.secondary',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover:not(.Mui-disabled)': {
                    color: '#4caf50',
                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                  },
                  '&.Mui-disabled': {
                    color: 'action.disabled'
                  }
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: '1.2rem',
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'rotate(180deg)'
                    }
                  }}
                />
              </IconButton>
            )}
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />


          {/* Role badge */}
          {user && (
            <Box sx={{
              mr: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                {user.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={getRoleLabel()}
                  variant="filled"
                  size="small"
                  sx={{
                    backgroundColor: isReadOnly() ? '#2196f3' : isAdmin() ? '#9c27b0' : '#2e7d32',
                    color: 'white',
                    height: 24
                  }}
                />
                <Chip
                  label={user.isSubUser ? 'Subbenutzer' : 'Benutzer'}
                  variant="filled"
                  size="small"
                  sx={{
                    backgroundColor: user.isSubUser ? '#ff9800' : '#4caf50',
                    color: 'white',
                    height: 24
                  }}
                />
              </Box>
            </Box>
          )}

          {/* User menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar
              src={user?.profilePicture}
              alt={user?.name}
              sx={{ width: 32, height: 32 }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        {isAdmin() && (
          <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
            <ListItemIcon>
              <AccountIcon />
            </ListItemIcon>
            Profil & Einstellungen
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { logout(); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Abmelden
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;